// Pieces the Storage lists and their dialogs share: one list layout under the Storage section header, the CLI line of a dialog, and the two ways a page writes: a toast-and-done action, and a dialog write that
// survives an uncertain outcome.
import {
  ApiError,
  classify,
  collectionPath,
  errorView,
  newIdempotencyKey,
  objectPath,
  useApi,
  type KObject,
  type StreamInfo,
} from "@nodus/api";
import { CopyButton, FilterBar, formatCount, LiveIndicator, toast, type LiveState } from "@nodus/ui";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { ListPage } from "../../app/templates";
import type { Scope } from "./model";

/** The Storage list layout: the section's tabs come from the nav registry; each view is a live list with a search. */
export function StorageListPage({
  cli,
  actions,
  query,
  noun,
  total,
  stream,
  paused,
  pending,
  onPausedChange,
  loading,
  error,
  onRetry,
  children,
}: {
  cli: string;
  actions?: React.ReactNode;
  query: string;
  /** Singular, lower case: "volume". */
  noun: string;
  total: number;
  stream: StreamInfo | undefined;
  paused: boolean;
  pending: number;
  onPausedChange: (paused: boolean) => void;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  // Storage lists always stream; "unwatched" is only for collections without a watch verb.
  const live: LiveState = stream === undefined || stream.state === "unwatched" ? "connecting" : stream.state;
  return (
    <ListPage
      title="Storage"
      description={noun === "volume" ? "Persistent files shared by your workspaces, jobs, and training runs." : noun === "secret" ? "Credentials your workloads can reference without putting them in code." : "Reusable container images for your workloads."}
      cli={cli}
      actions={actions}
      loading={loading}
      error={error}
      onRetry={onRetry}
      filters={
        <FilterBar
          query={query}
          onQueryChange={(v) => void navigate({ to: ".", search: (s: Record<string, unknown>) => ({ ...s, q: v || undefined }), replace: true })}
          placeholder={`Search ${noun}s`}
          right={<LiveIndicator state={live} paused={paused} pendingChanges={pending} onPausedChange={onPausedChange} {...(stream?.lastEventAt ? { lastEventAt: stream.lastEventAt } : {})} />}
        />
      }
      count={`${formatCount(total)} ${total === 1 ? noun : `${noun}s`}`}
    >
      {children}
    </ListPage>
  );
}

/** The grid of a dialog's form: one column that may shrink, so a long command scrolls instead of widening the dialog. */
export const DIALOG_FORM = "grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4";

/** The equivalent command of a dialog, with Copy. Secret values never appear in it. */
export function CliLine({ command }: { command: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2">
      <code tabIndex={0} className="min-w-0 flex-1 overflow-x-auto font-mono text-mono whitespace-nowrap text-fg-secondary">
        {command}
      </code>
      <CopyButton text={command} label="Copy command" />
    </div>
  );
}

/** Runs a write and says how it went: a toast either way, `busy` while it runs. Resolves true on success. */
export function useRun() {
  const [busy, setBusy] = React.useState(false);
  const run = React.useCallback(async (fn: () => Promise<unknown>, done: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      toast(done);
      return true;
    } catch (err) {
      toast.error(errorView(err).title);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, run };
}

export type SubmitState = { state: "idle" } | { state: "sending" } | { state: "error"; error: unknown } | { state: "uncertain"; requestId?: string };

/**
 * Sends one write from a dialog. The Idempotency-Key lasts as long as the outcome is uncertain, so a retry after a
 * timeout can never write twice; any other answer starts the next attempt on a fresh key.
 */
export function useSubmit<B, R>(send: (body: B, idempotencyKey: string) => Promise<R>) {
  const [state, setState] = React.useState<SubmitState>({ state: "idle" });
  const [key, setKey] = React.useState(newIdempotencyKey);

  const submit = async (body: B): Promise<R | undefined> => {
    setState({ state: "sending" });
    try {
      const result = await send(body, key);
      setKey(newIdempotencyKey());
      setState({ state: "idle" });
      return result;
    } catch (err) {
      if (classify(err, true) === "uncertain") {
        setState({ state: "uncertain", ...(err instanceof ApiError && err.requestId ? { requestId: err.requestId } : {}) });
      } else {
        setKey(newIdempotencyKey());
        setState({ state: "error", error: err });
      }
      return undefined;
    }
  };
  return { state, submit, idempotencyKey: key, settle: () => setState({ state: "idle" }) };
}

/** Creates one object from a dialog; `check` reads it back by name after an uncertain outcome. */
export function useCreate(kind: string, scope: Scope) {
  const { client } = useApi();
  const write = useSubmit(async (body: KObject, key: string) => (await client.post<KObject>(collectionPath(kind, scope.project), body, { org: scope.org, idempotencyKey: key })).data);
  const check = async (name: string): Promise<KObject | undefined> => {
    write.settle();
    return client.get<KObject>(objectPath(kind, name, scope.project), { org: scope.org }).catch(() => undefined);
  };
  return { ...write, check };
}
